import { useState, type ReactNode } from "react";
import { XIcon } from "lucide-react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { Card, CardDescription, CardTitle } from "../ui/card";
import { resolvePersonAvatarUrl } from "./morrowPersonAvatarCatalog";

export type MorrowPersonDetailInfo = {
  readonly email: string;
};

export const PERSON_AVATAR_RADIUS = "rounded-xl";

const PERSON_AVATAR_SIZES = {
  xs: "size-7",
  sm: "size-6",
  md: "size-10",
  lg: "size-16",
  xl: "size-24",
} as const;

export type PersonAvatarSize = keyof typeof PERSON_AVATAR_SIZES;

/** Portrait image for a person — Clerk photo when present, otherwise a generated avatar. */
export function clerkPortraitImageUrl(
  source: { hasImage?: boolean; imageUrl?: string | null } | null | undefined,
): string | undefined {
  if (!source || source.hasImage !== true) return undefined;
  const trimmed = source.imageUrl?.trim();
  return trimmed || undefined;
}

export function personInitials(name: string): string {
  const parts = name
    .trim()
    .split(/[\s@._-]+/)
    .filter((part) => part.length > 0);
  const first = parts[0];
  if (!first) return "?";
  const second = parts[1];
  if (!second) return first.slice(0, 1).toUpperCase();
  return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase();
}

export function personRoleLine(title: string, role?: string): string {
  const trimmedTitle = title.trim();
  const trimmedRole = role?.trim() ?? "";
  if (trimmedTitle && trimmedRole && trimmedTitle !== trimmedRole) {
    return `${trimmedTitle} · ${trimmedRole}`;
  }
  return trimmedTitle || trimmedRole;
}

export function MorrowPersonAvatar({
  name,
  imageUrl,
  avatarSeed,
  size = "md",
  large = false,
  className,
}: {
  readonly name: string;
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
  readonly size?: PersonAvatarSize;
  /** @deprecated Prefer `size="lg"`. */
  readonly large?: boolean;
  readonly className?: string;
}) {
  const resolvedSize = large ? "lg" : size;
  const sizeClass = PERSON_AVATAR_SIZES[resolvedSize];
  const [portraitFailed, setPortraitFailed] = useState(false);
  const src = resolvePersonAvatarUrl(name, {
    ...(portraitFailed || !imageUrl?.trim() ? {} : { imageUrl }),
    ...(avatarSeed ? { avatarSeed } : {}),
  });

  return (
    <img
      alt=""
      aria-hidden="true"
      src={src}
      onError={() => {
        if (imageUrl?.trim() && !portraitFailed) setPortraitFailed(true);
      }}
      className={cn("shrink-0 object-cover", PERSON_AVATAR_RADIUS, sizeClass, className)}
    />
  );
}

function PersonLines({
  name,
  title,
  role,
  description,
}: {
  readonly name: string;
  readonly title: string;
  readonly role: string;
  readonly description: string;
}) {
  const roleLine = personRoleLine(title, role);
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
      <CardTitle className="truncate text-sm leading-none">{name}</CardTitle>
      {roleLine ? (
        <p className="truncate text-xs text-muted-foreground">{roleLine}</p>
      ) : (
        <CardDescription className="line-clamp-2 text-pretty">{description}</CardDescription>
      )}
    </div>
  );
}

export function MorrowPersonCard({
  name,
  description,
  title = "",
  role = "",
  imageUrl,
  avatarSeed,
  pending = false,
  layout = "tile",
  action,
  onClick,
  ariaLabel,
}: {
  readonly name: string;
  readonly description: string;
  readonly title?: string;
  readonly role?: string;
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
  readonly pending?: boolean;
  readonly layout?: "tile" | "row";
  readonly action?: ReactNode;
  readonly onClick?: () => void;
  readonly ariaLabel?: string;
}) {
  if (layout === "row") {
    return (
      <div className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 text-left">
        <MorrowPersonAvatar
          name={name}
          {...(imageUrl ? { imageUrl } : {})}
          {...(avatarSeed ? { avatarSeed } : {})}
        />
        <PersonLines name={name} title={title} role={role} description={description} />
        {action}
      </div>
    );
  }

  return (
    <Card
      render={
        onClick ? <button type="button" aria-label={ariaLabel} onClick={onClick} /> : undefined
      }
      className={cn(
        "h-full w-full flex-row items-center gap-3.5 border-border p-3.5 text-left",
        pending && "border-dashed",
        onClick &&
          "cursor-pointer transition-colors hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70",
      )}
    >
      <MorrowPersonAvatar
        name={name}
        {...(imageUrl ? { imageUrl } : {})}
        {...(avatarSeed ? { avatarSeed } : {})}
      />
      <PersonLines name={name} title={title} role={role} description={description} />
      {action}
    </Card>
  );
}

export function MorrowPersonDetailCard({
  name,
  title,
  role,
  info,
  imageUrl,
  avatarSeed,
  canEdit,
  onEdit,
}: {
  readonly name: string;
  readonly title: string;
  readonly role: string;
  readonly description: string;
  readonly info: MorrowPersonDetailInfo;
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
  readonly canEdit: boolean;
  readonly onEdit: () => void;
}) {
  const { t } = useI18n();
  const roleLine = personRoleLine(title, role);
  const email = info.email.trim();

  return (
    <div className="flex min-w-0 flex-col gap-4 pe-8">
      <div className="flex items-start gap-3.5">
        <MorrowPersonAvatar
          name={name}
          {...(imageUrl ? { imageUrl } : {})}
          {...(avatarSeed ? { avatarSeed } : {})}
          size="lg"
          className="size-20"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
          <h2 className="truncate text-base font-semibold leading-tight text-foreground">{name}</h2>
          {roleLine ? <p className="truncate text-sm text-muted-foreground">{roleLine}</p> : null}
          {email && email !== name.trim() ? (
            <p className="truncate text-sm text-muted-foreground">{email}</p>
          ) : null}
        </div>
      </div>
      {canEdit ? (
        <div className="flex justify-end">
          <Button size="sm" type="button" variant="outline" onClick={onEdit}>
            {t("team.edit")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function MorrowPersonRemoveButton({
  label,
  onClick,
}: {
  readonly label: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      aria-label={label}
      className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      type="button"
      onClick={onClick}
    >
      <XIcon className="size-3.5" />
    </button>
  );
}
